import './processShim';
import './locales/i18n';
import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from "@sentry/react";
import { Provider } from 'react-redux';
import store, { swInstalled, swHasUpdate } from './redux';
import App from './containers/App/App';
import registerServiceWorker from './registerServiceWorker';
import { install_third_party_warning_filter } from './dev/suppressThirdPartyWarnings';


import { BrowserRouter as Router } from 'react-router-dom'
import { computeBasename } from './routing/contextUrl';

import 'semantic-ui-css/semantic.min.css'
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '@fortawesome/fontawesome-free/css/v4-shims.css';
import './index.css';
import './treeSelect.css';

import infos from '../package.json';
import TimeAgo from 'javascript-time-ago';
import fr from 'javascript-time-ago/locale/fr';
import en from 'javascript-time-ago/locale/en';
import de from 'javascript-time-ago/locale/de';

// The shell recovery script in index.html asks the page whether the entry
// module ran. Set this before any app work so an open tab is not reloaded
// as if the bundle had 404'd.
window.__kronosBooted = true;

TimeAgo.addDefaultLocale(fr)
TimeAgo.addLocale(en)
TimeAgo.addLocale(de)

if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                maskAllInput: false,
                blockAllMedia: false
            })
        ],
        environment: process.env.NODE_ENV,
        release: infos.version,

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: process.env.NODE_ENV === 'development' ? 1.0 : 0.25,

        // Capture Replay for 10% of all sessions,
        // plus for 100% of sessions with an error
        // Learn more at
        // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    });
}

const container = document.getElementById('root');
if (!container) {
    throw new Error('Root element #root not found');
}

install_third_party_warning_filter();

// Instance/edition context lives in the URL as a path prefix (/i/:slug[/e/:id]).
// Feeding it as the router basename keeps every existing logical route, link and
// `navigate('/...')` call working unchanged: React Router strips the prefix from
// `useLocation()` and re-adds it on navigation. Computed once from the initial URL;
// any instance/edition change performs a full reload (see ContextGate / selectors),
// so the basename is always coherent for the current document.
const basename = computeBasename();

const root = createRoot(container);
root.render(
    <Provider store={store}>
        {/* Omit v7_startTransition: heavy views (e.g. React Flow) could defer location updates vs the URL bar. */}
        <Router basename={basename} future={{ v7_relativeSplatPath: true }}>
            <App/>
        </Router>
    </Provider>
);

registerServiceWorker({
    reset: false,
    onInstall: reg => store.dispatch(swInstalled(reg)),
    onUpdate: reg => store.dispatch(swHasUpdate(reg)),
});

// Dev helper: open the update modal without a service-worker update.
// Example: http://localhost:3000/?swUpdate=1 (carousel uses the last illustrated changelog entries).
if (process.env.NODE_ENV !== 'production') {
    try {
        if (new URLSearchParams(window.location.search).has('swUpdate'))
            store.dispatch(swHasUpdate(null));
    } catch (e) {
        // ignore
    }
}
